import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Search } from 'lucide-react'

import { ScheduleSideDrawer } from '@/components/calendar/ScheduleSideDrawer'
import { fetchAppointments } from '@/lib/api/appointments'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import {
  appointmentGuestLabel,
  appointmentServiceLabel,
  isGuestSearchQuery,
  lookupRangeForGuestSearch,
  occupyingAppointments,
} from '@/lib/calendar/appointmentLookup'
import { formatAppointmentStatus } from '@/lib/appointmentStatusLabels'
import { formatArrivalTime } from '@/lib/calendar/checkIn'

/**
 * @param {{
 *   open: boolean
 *   onClose: () => void
 *   initialQuery?: string
 *   onSelectAppointment: (appointment: import('@/types/api').Appointment) => void
 * }} props
 */
export function BookingSearchDrawer({ open, onClose, initialQuery = '', onSelectAppointment }) {
  const [query, setQuery] = useState(initialQuery)
  const [appointments, setAppointments] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  function handleClose() {
    setQuery('')
    setAppointments([])
    setError('')
    onClose()
  }

  const guestSearch = isGuestSearchQuery(query)

  const load = useCallback(async () => {
    if (!guestSearch) {
      setAppointments([])
      setError('')
      return
    }

    setLoading(true)
    setError('')

    try {
      const range = lookupRangeForGuestSearch()
      const items = await fetchAppointments({
        ...range,
        q: query.trim(),
      })
      setAppointments(occupyingAppointments(items))
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to search bookings.'))
      setAppointments([])
    } finally {
      setLoading(false)
    }
  }, [guestSearch, query])

  useEffect(() => {
    if (!open) {
      return undefined
    }

    const timer = window.setTimeout(() => {
      load()
    }, 250)

    return () => window.clearTimeout(timer)
  }, [load, open])

  const results = useMemo(() => appointments.slice(0, 40), [appointments])

  return (
    <ScheduleSideDrawer
      open={open}
      onClose={handleClose}
      title="Find booking"
      subtitle="Search by guest name, phone, or booking details"
    >
      <label className="relative mb-4 block">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          value={query}
          autoFocus
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search guest, phone…"
          className="h-10 w-full rounded-xl border border-border/70 bg-background pl-9 pr-3 text-sm text-foreground outline-none ring-primary/30 focus:ring-2"
          aria-label="Search bookings"
        />
      </label>

      {!guestSearch ? (
        <p className="text-sm text-muted-foreground">Type at least 2 characters to search.</p>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Searching…
        </div>
      ) : null}

      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      {!loading && guestSearch && results.length === 0 && !error ? (
        <p className="py-6 text-sm text-muted-foreground">No bookings found for “{query.trim()}”.</p>
      ) : null}

      <ul className="space-y-2">
        {results.map((appointment) => (
          <li key={appointment.id}>
            <button
              type="button"
              className="w-full rounded-xl border border-border/60 bg-card px-3 py-3 text-left transition-colors hover:border-primary/30 hover:bg-primary/[0.04]"
              onClick={() => {
                onSelectAppointment(appointment)
                handleClose()
              }}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="truncate text-sm font-semibold text-foreground">
                  {appointmentGuestLabel(appointment)}
                </p>
                <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {formatAppointmentStatus(appointment.status)}
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {formatArrivalTime(appointment.starts_at)} · {appointmentServiceLabel(appointment)}
              </p>
              {appointment.stylist?.name ? (
                <p className="mt-0.5 text-[11px] text-muted-foreground">{appointment.stylist.name}</p>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
    </ScheduleSideDrawer>
  )
}
